import type { TravelFormDesktopProps } from '@/features/travel-form/travel-form-desktop/travel-form-desktop';
import type { TravelFormV1Attributes } from '@/features/travel-form/travel-form-v1/travel-form-v1-type';

import Banner from '@/core/components/banner/banner';
import { HtmlTextWrapper, IconImage } from '@/core/features';
import color from '@/core/features/styles/color';
import HeaderMapButtonDesktop from '@/features/map/header-map-button-desktop/header-map-button-desktop';
import TravelFormDesktop from '@/features/travel-form/travel-form-desktop/travel-form-desktop';

type TravelFormV1DesktopProps = {
    banner: TravelFormV1Attributes['banner'];
    isWidget: boolean;
} & TravelFormDesktopProps;

export default function TravelFormV1Desktop({
    activityRequestTarget,
    airportRequestTarget,
    banner,
    dateRequestTarget,
    destinationChangeRequestTarget,
    destinationRequestTarget,
    formConfiguration,
    isSticky,
    isWidget,
    onOverlayClose,
    onOverlayCtaClick,
    openOverlay,
    overlayType,
    travelForm,
}: TravelFormV1DesktopProps) {
    const numberOfFields = Object.values(formConfiguration.fields || {}).filter((field) => !!field).length;
    if (!banner) {
        return (
            <TravelFormDesktop
                activityRequestTarget={activityRequestTarget}
                airportRequestTarget={airportRequestTarget}
                dateRequestTarget={dateRequestTarget}
                destinationChangeRequestTarget={destinationChangeRequestTarget}
                destinationRequestTarget={destinationRequestTarget}
                formConfiguration={formConfiguration}
                isSticky={isSticky}
                isWidget={isWidget}
                onOverlayClose={onOverlayClose}
                onOverlayCtaClick={onOverlayCtaClick}
                openOverlay={openOverlay}
                overlayType={overlayType}
                travelForm={travelForm}
            />
        );
    }

    return (
        <div className={'w-full'}>
            <Banner
                className={'flex flex-col justify-center z-2'}
                height={banner.image ? 500 : 330}
                image={banner.image}
                imageOverlayColor={banner.image ? color('check24Blue-04') : undefined}
                marginBottom={banner.image ? -15 : 0}
            >
                <div
                    className={'flex flex-col justify-center self-center w-full'}
                    style={{ maxWidth: numberOfFields > 2 ? '1280px' : '1072px' }}
                >
                    <div className={`${banner.image ? 'w-full' : 'flex justify-between items-center'}`}>
                        <div
                            className={`self-start z-1 gap-15 ${banner.htmlTitle ? 'flex flex-col items-start justify-center' : 'flex flex-col items-center justify-center'} ${banner.image ? 'text-white' : 'mt-30'}`}
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            style={{ fontSize: 30, marginBottom: 55 }}
                        >
                            {banner.htmlTitle ? (
                                <h1>
                                    <HtmlTextWrapper htmlText={banner.htmlTitle} />
                                </h1>
                            ) : (
                                banner.title && <h1 className={'font-bold'}>{banner.title}</h1>
                            )}
                            {banner.subtitle && <h2 className={'text-18 wrap-break-word'}>{banner.subtitle}</h2>}
                        </div>
                        {!banner.signet && !banner.image && (
                            <div
                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                style={{ height: 228 }}
                            ></div>
                        )}
                        {banner.signet && (
                            <div className={'mb-25'}>
                                <IconImage
                                    alt={banner.signet.imageAlt}
                                    height={200}
                                    inline={true}
                                    url={banner.signet.imageUrl}
                                    width={200}
                                />
                            </div>
                        )}
                    </div>
                    <div className={'flex flex-col justify-center items-center'}>
                        <TravelFormDesktop
                            activityRequestTarget={activityRequestTarget}
                            airportRequestTarget={airportRequestTarget}
                            dateRequestTarget={dateRequestTarget}
                            destinationChangeRequestTarget={destinationChangeRequestTarget}
                            destinationRequestTarget={destinationRequestTarget}
                            disableCtaTooltip={true}
                            formConfiguration={formConfiguration}
                            isSticky={isSticky}
                            isWidget={isWidget}
                            onOverlayClose={onOverlayClose}
                            onOverlayCtaClick={onOverlayCtaClick}
                            openOverlay={openOverlay}
                            overlayType={overlayType}
                            travelForm={travelForm}
                        />
                        {banner.mapButton && (
                            <div
                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                style={{ marginTop: 73 }}
                            >
                                <HeaderMapButtonDesktop
                                    iconUrl={banner.mapButton.iconUrl}
                                    linkUrl={banner.mapButton.linkUrl}
                                    title={banner.mapButton.title}
                                    width={'160px'}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Banner>
            {!banner.image && (
                <div
                    className={'relative'}
                    style={{
                        /* eslint-disable no-inline-styles/no-inline-styles */
                        borderTop: `1px solid ${color('alto')}`,
                        left: '50%',
                        marginLeft: 'min(calc(-1024px / 2), -50vw)',
                        marginTop: 33,
                        width: 'max(1024px, 100vw)',
                        /* eslint-enable no-inline-styles/no-inline-styles */
                    }}
                />
            )}
        </div>
    );
}
